import { getToken, setToken, removeToken } from '../utils/tokenStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StaticPages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [groupForm, setGroupForm] = useState({ request_type: 'swap', group_id: '', property_id: '', message: '' });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [myGroups, setMyGroups] = useState([]);

  const fetchUserData = useCallback(async () => {
    const token = getToken();
    if (token) {
      const config = { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
      axios.get(`${API}/auth/me`, config).then(res => setCurrentUser(res.data)).catch(() => {});
      axios.get(`${API}/groups`, config).then(res => setMyGroups(res.data.filter(g => g.members?.some(m => m.user_id)))).catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const submitGroupRequest = async (e) => {
    e.preventDefault();
    if (!groupForm.message.trim()) { alert('Please describe your request'); return; }
    setFormSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${API}/support/group-request`, groupForm, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setFormSuccess(true);
      setGroupForm({ request_type: 'swap', group_id: '', property_id: '', message: '' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit request');
    }
    setFormSubmitting(false);
  };

  return (
    <div className="static-page" data-testid="support-page">
      <header className="static-header">
        <div className="container">
          <div className="header-content">
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" onClick={() => navigate('/')} />
            <nav>
              <button className="btn btn-secondary" onClick={() => navigate('/properties')}>Browse Properties</button>
              <button className="btn btn-primary" onClick={() => navigate('/apply')}>Apply Now</button>
            </nav>
          </div>
        </div>
      </header>

      <section className="page-hero">
        <div className="container">
          <h1>Help & Support</h1>
          <p>We're here to help you every step of the way.</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container">
          {/* Group Support Form - Only for logged-in members */}
          {currentUser && (
            <div className="content-section" style={{ background: '#f0f7ff', borderLeft: '4px solid #2563eb' }} data-testid="group-support-form-section">
              <h2 style={{ borderBottom: 'none', paddingBottom: '8px' }}>Group Support</h2>
              <p>Need help with your group? Want to swap, be removed, or report an issue? Submit a request and our team will assist you.</p>
              {formSuccess ? (
                <div style={{ background: '#d1fae5', padding: '16px', borderRadius: '8px', color: '#059669', fontWeight: '600' }} data-testid="group-support-success">
                  Your request has been submitted! We'll get back to you soon.
                  <button className="btn btn-secondary" style={{ marginLeft: '16px', padding: '6px 14px', fontSize: '13px' }} onClick={() => setFormSuccess(false)}>Submit Another</button>
                </div>
              ) : (
                <form onSubmit={submitGroupRequest} style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '500px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Request Type</label>
                    <select value={groupForm.request_type} onChange={e => setGroupForm({ ...groupForm, request_type: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} data-testid="group-support-type">
                      <option value="swap">Swap Group</option>
                      <option value="removal">Remove Me from Group</option>
                      <option value="issue">Report Group Issue</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {myGroups.length > 0 && (
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Related Group (optional)</label>
                      <select value={groupForm.group_id} onChange={e => setGroupForm({ ...groupForm, group_id: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} data-testid="group-support-group">
                        <option value="">Select a group...</option>
                        {myGroups.map(g => <option key={g.group_id} value={g.group_id}>{g.name} — {g.property_city}, {g.property_state}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Describe Your Request</label>
                    <textarea value={groupForm.message} onChange={e => setGroupForm({ ...groupForm, message: e.target.value })} rows={4} placeholder="Tell us what you need help with..." style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', resize: 'vertical' }} data-testid="group-support-message" />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: 'fit-content', padding: '10px 24px' }} disabled={formSubmitting} data-testid="group-support-submit">
                    {formSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Support Categories */}
          <div className="content-section">
            <h2>How Can We Help?</h2><div className="support-categories">
              {[
                { title: "Getting Started", description: "New to House Sharing Seniors? Learn how to apply and get started.", link: "/faq" },
                { title: "Account Help", description: "Manage your profile, update preferences, or reset your password.", link: "/contact" },
                { title: "Property Questions", description: "Help with browsing properties or listing your own property.", link: "/faq" },
                { title: "Finding Housemates", description: "Tips for finding compatible housemates and making connections.", link: "/faq" },
                { title: "Safety & Security", description: "Learn about our safety measures and how to stay secure.", link: "/faq" },
                { title: "Contact Support", description: "Can't find your answer? Talk to our friendly support team.", link: "/contact" }
              ].map((category, index) => (
                <div className="support-category" key={category.link || index} onClick={() => navigate(category.link)} data-testid={`support-category-${index}`}>
                  <h4>{category.title}</h4>
                  <p>{category.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="content-section">
            <h2>Quick Links</h2>
            <ul>
              <li><a href="/faq" style={{ color: '#2563eb' }}>Frequently Asked Questions</a></li>
              <li><a href="/about" style={{ color: '#2563eb' }}>About House Sharing Seniors</a></li>
              <li><a href="/privacy" style={{ color: '#2563eb' }}>Privacy Policy</a></li>
              <li><a href="/terms" style={{ color: '#2563eb' }}>Terms of Service</a></li>
              <li><a href="/resources" style={{ color: '#2563eb' }}>Resources & Downloads</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="content-section">
            <h2>Contact Our Support Team</h2>
            <div className="contact-info-grid">
              <div className="contact-info-item">
                <div>
                  <h4 style={{ fontSize: '20px' }}>Phone Support</h4>
                  <p style={{ fontSize: '20px', fontWeight: '700' }}><a href="tel:1300775059" style={{ color: '#1a2332' }}>1300 77 5059</a></p>
                  <p style={{ fontSize: '16px' }}>Mon–Fri 9am–5pm, Sat 10am–4pm</p>
                  <p style={{ fontSize: '16px' }}>SMS: <a href="sms:0485836499" style={{ color: '#2563eb' }}>0485 836 499</a></p>
                </div>
              </div>
              <div className="contact-info-item">
                <div>
                  <h4 style={{ fontSize: '20px' }}>Email Support</h4>
                  <p style={{ fontSize: '18px' }}><a href="mailto:help@housesharingseniors.com.au" style={{ color: '#2563eb' }}>help@housesharingseniors.com.au</a></p>
                  <p style={{ fontSize: '16px' }}>We respond within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency */}
          <div className="content-section" style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b' }}>
            <h2 style={{ borderBottom: 'none', paddingBottom: 0 }}>Emergency Contacts</h2>
            <p><strong>If you feel unsafe or in danger, please contact emergency services:</strong></p>
            <ul>
              <li><strong>Emergency:</strong> 000</li>
              <li><strong>Police Non-Emergency:</strong> 131 444</li>
              <li><strong>Lifeline:</strong> 13 11 14</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="static-footer">
        <div className="container">
          <p>&copy; 2026 House Sharing Seniors. All rights reserved.</p>
          <p className="footer-links">
            <a href="/privacy">Privacy Policy</a> |
            <a href="/terms">Terms of Service</a> |
            <a href="/contact">Contact Us</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SupportPage;
